import "./login.style.scss";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { FaUser } from "react-icons/fa";

import { PasswordInputField } from "./login.page";
import FormError from "../../components/form-error/form-error.component";
import SuccessDisplay from "../../components/success-display/success-display.component";
import { useUpdateCompanyIdentifications } from "../../queries/auth.queries";

const verificationSchema = yup.object().shape({
	username: yup.string().required("Username cannot be empty"),
	password: yup.string().required("Password cannot be empty"),
});

export default function Verification() {
	const updateIdentificationsMutation = useUpdateCompanyIdentifications();
	const navigate = useNavigate();

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm({
		defaultValues: { username: "", password: "" },
		mode: "onSubmit",
		resolver: yupResolver(verificationSchema),
	});

	// Brief SuccessDisplay before navigating away — same ~1.2s pattern used
	// elsewhere for a mutation that finishes by leaving the current view.
	const navigateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	useEffect(() => {
		return () => {
			if (navigateTimeoutRef.current) clearTimeout(navigateTimeoutRef.current);
		};
	}, []);

	const handleIRDAccountSave = (verificationData: {
		username: string;
		password: string;
	}) => {
		updateIdentificationsMutation.mutate(
			{ ...verificationData, verification: true },
			{
				onSuccess: () => {
					navigateTimeoutRef.current = setTimeout(() => navigate("/"), 1200);
				},
			},
		);
	};

	return (
		<div className="login-wrapper">
			<div className="login-container">
				<div className="login-container-inner">
					<div className="form-container">
						{updateIdentificationsMutation.isSuccess ? (
							<SuccessDisplay
								title="IRD account verified"
								description="Redirecting you to the dashboard..."
							/>
						) : (
							<>
								<h2 className="title-text">IRD Account</h2>

								<p className="description-text">
									Please enter your IRD credentials to continue. We
									can't process your request until verification is
									complete.
								</p>

								<FormError
									isError={updateIdentificationsMutation.isError}
									error={updateIdentificationsMutation.error}
									fallback="Verification failed."
								/>

								<div className="form-inputs-container">
									<div className="input-wrapper">
										<p className="input-label">IRD username</p>
										<div className="input-wrapper-inner">
											<FaUser />
											<input
												{...register("username")}
												type="text"
												className="input-container"
												placeholder="Username"
											/>
										</div>
										<span className="form-validation-error">
											{errors?.username?.message}
										</span>
									</div>
									<PasswordInputField
										{...register("password")}
										placeholder="Password"
										error={errors?.password?.message}
										label="Password"
									/>
								</div>

								<div
									className="form-submit-btn"
									onClick={handleSubmit(handleIRDAccountSave)}
								>
									Save Credentials
								</div>
							</>
						)}
					</div>
					<div className="description-container">
						<h2 className="title-text inverted">IRD Verification</h2>
						<p className="description-text">
							Enter your IRD account details, so you can start
							e-billing. <br />
							<br />
							Make sure they match the credentials registered on
							the IRD server.
						</p>
					</div>
				</div>
			</div>
		</div>
	);
}
